import { getIntersectionRoot, getScrollEventTarget } from '@theme/scroll-container';

class TootoFeaturedProducts extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabs = Array.from(this.querySelectorAll('[data-tooto-product-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-tooto-product-panel]'));
    this.stage = this.querySelector('.tooto-featured-products__stage');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 768px)');
    this.reduceMotion = this.reduceMotionQuery.matches;
    this.scrollSwitchRequested = this.dataset.scrollSwitch === 'true' && this.tabs.length > 1;
    this.scrollSwitchActive = false;
    this.scrollFrame = 0;
    this.productTransitionId = 0;
    this.imageCache = new Map();
    this.imageRequestIds = new WeakMap();

    this.handleTabClick = (event) => this.selectProduct(event.currentTarget.dataset.tootoProductTab);
    this.handleTabKeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      nextTab?.focus();
      this.selectProduct(nextTab?.dataset.tootoProductTab);
    };
    this.handleSwatchClick = (event) => this.selectVariant(event.currentTarget);
    this.handleSwatchIntent = (event) => this.preloadSwatchImage(event.currentTarget);
    this.handleScroll = () => this.queueScrollUpdate();
    this.handleViewportChange = () => {
      this.reduceMotion = this.reduceMotionQuery.matches;
      this.bindScrollTarget();
      this.updateScrollMode();
      this.queueScrollUpdate();
    };

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleTabKeydown);
    });

    this.swatches = Array.from(this.querySelectorAll('[data-tooto-variant]'));
    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClick);
      swatch.addEventListener('pointerenter', this.handleSwatchIntent);
      swatch.addEventListener('focus', this.handleSwatchIntent);
    });

    this.selectProduct(this.dataset.initialProduct, false);
    this.updateScrollMode();

    this.bindScrollTarget();
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    this.desktopQuery.addEventListener?.('change', this.handleViewportChange);
    this.reduceMotionQuery.addEventListener?.('change', this.handleViewportChange);
    this.queueScrollUpdate();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-visible');
        this.observer.disconnect();
      },
      { root: getIntersectionRoot(), threshold: 0.12 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleTabKeydown);
    });
    this.swatches?.forEach((swatch) => {
      swatch.removeEventListener('click', this.handleSwatchClick);
      swatch.removeEventListener('pointerenter', this.handleSwatchIntent);
      swatch.removeEventListener('focus', this.handleSwatchIntent);
    });
    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleViewportChange);
    this.desktopQuery?.removeEventListener?.('change', this.handleViewportChange);
    this.reduceMotionQuery?.removeEventListener?.('change', this.handleViewportChange);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.observer?.disconnect();
  }

  bindScrollTarget() {
    const nextScrollTarget = getScrollEventTarget();
    if (this.scrollTarget === nextScrollTarget) return;

    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.scrollTarget = nextScrollTarget;
    this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  updateScrollMode() {
    const shouldEnable = this.scrollSwitchRequested && this.desktopQuery.matches;
    if (shouldEnable === this.scrollSwitchActive) return;

    this.scrollSwitchActive = shouldEnable;
    this.classList.toggle('is-scroll-switch-enabled', shouldEnable);

    if (shouldEnable) {
      this.selectProduct(this.tabs[0]?.dataset.tootoProductTab, false);
    }
  }

  queueScrollUpdate() {
    if (!this.scrollSwitchActive || this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateScrollProduct();
    });
  }

  updateScrollProduct() {
    if (!this.scrollSwitchActive || !this.stage || this.tabs.length < 2) return;

    const rootRect = this.getBoundingClientRect();
    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeight =
      Number.parseFloat(rootStyles.getPropertyValue('--header-group-height')) ||
      Number.parseFloat(rootStyles.getPropertyValue('--header-height')) ||
      0;
    const stickyTop = Number.parseFloat(getComputedStyle(this.stage).top) || headerHeight;
    const scrollRange = Math.max(1, this.offsetHeight - this.stage.offsetHeight);
    const progress = Math.min(1, Math.max(0, (stickyTop - rootRect.top) / scrollRange));
    const firstKey = this.tabs[0]?.dataset.tootoProductTab;
    const secondKey = this.tabs[1]?.dataset.tootoProductTab;
    const switchingToSecond = this.dataset.activeProduct !== secondKey;
    const targetKey = progress >= (switchingToSecond ? 0.42 : 0.34) ? secondKey : firstKey;

    if (targetKey && targetKey !== this.dataset.activeProduct) this.selectProduct(targetKey);
  }

  selectProduct(productKey, animate = true) {
    if (!productKey) return;

    const previousKey = this.dataset.activeProduct;
    const previousPanel = this.panels.find((panel) => panel.dataset.tootoProductPanel === previousKey);
    const nextPanel = this.panels.find((panel) => panel.dataset.tootoProductPanel === productKey);

    if (!nextPanel || previousKey === productKey) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tootoProductTab === productKey;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.dataset.activeProduct = productKey;

    const useScrollTransition =
      animate &&
      this.scrollSwitchActive &&
      !this.reduceMotion &&
      previousPanel &&
      typeof nextPanel.animate === 'function';

    if (!useScrollTransition) {
      this.panels.forEach((panel) => {
        const isActive = panel === nextPanel;
        panel.getAnimations().forEach((animation) => animation.cancel());
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.style.removeProperty('z-index');
        if (isActive && animate) {
          panel.classList.remove('is-entering');
          requestAnimationFrame(() => panel.classList.add('is-entering'));
        }
      });
      return;
    }

    const transitionId = ++this.productTransitionId;
    this.classList.add('is-product-switching');

    this.panels.forEach((panel) => {
      panel.getAnimations().forEach((animation) => animation.cancel());
      panel.classList.remove('is-entering');
      const participates = panel === previousPanel || panel === nextPanel;
      panel.hidden = !participates;
      panel.setAttribute('aria-hidden', String(panel !== nextPanel));
      panel.style.zIndex = panel === nextPanel ? '2' : '1';
    });

    const outgoing = previousPanel.animate(
      [
        { opacity: 1, transform: 'translateY(0) scale(1)' },
        { opacity: 0, transform: 'translateY(-14px) scale(.985)' },
      ],
      { duration: 480, easing: 'cubic-bezier(.32, 0, .2, 1)', fill: 'forwards' }
    );
    const incoming = nextPanel.animate(
      [
        { opacity: 0, transform: 'translateY(20px) scale(.985)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 680, delay: 70, easing: 'cubic-bezier(.16, 1, .3, 1)', fill: 'both' }
    );

    Promise.allSettled([outgoing.finished, incoming.finished]).then(() => {
      if (transitionId !== this.productTransitionId) return;

      this.panels.forEach((panel) => {
        panel.getAnimations().forEach((animation) => animation.cancel());
        const isActive = panel === nextPanel;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.style.removeProperty('z-index');
      });
      this.classList.remove('is-product-switching');
    });
  }

  selectVariant(swatch) {
    const panel = swatch.closest('[data-tooto-product-panel]');
    if (!panel) return;

    panel.querySelectorAll('[data-tooto-variant]').forEach((button) => {
      const isSelected = button === swatch;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    const colorValue = panel.querySelector('[data-tooto-color-value]');
    if (colorValue) {
      colorValue.textContent = swatch.dataset.colorName || '';
      this.animateValue(colorValue);
    }

    const variantInput = panel.querySelector('[data-tooto-variant-input]');
    if (variantInput && swatch.dataset.variantId) variantInput.value = swatch.dataset.variantId;

    const price = panel.querySelector('[data-tooto-product-price]');
    const priceTemplate = panel.querySelector(`[data-tooto-variant-price="${swatch.dataset.variantId}"]`);
    if (price && priceTemplate instanceof HTMLTemplateElement) {
      price.replaceChildren(priceTemplate.content.cloneNode(true));
      this.animateValue(price);
    }

    const preorderBadge = panel.querySelector('[data-tooto-featured-preorder-badge]');
    if (preorderBadge) {
      const isPreorder = swatch.dataset.preorder === 'true';
      preorderBadge.dataset.nativePreorder = String(isPreorder);
      preorderBadge.hidden = !isPreorder;
    }

    const addButton = panel.querySelector('button[name="add"]');
    const isAvailable = swatch.dataset.available === 'true';
    if (addButton) {
      addButton.disabled = !isAvailable;
      const labelNode = Array.from(addButton.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      );
      if (labelNode) labelNode.nodeValue = `\n        ${isAvailable ? this.dataset.addText : this.dataset.soldOutText}\n        `;
    }

    const image = panel.querySelector('[data-tooto-product-image]');
    if (image && swatch.dataset.imageSrc) {
      this.switchImage(image, swatch);
    }

    const addToCart = panel.querySelector('add-to-cart-component');
    if (addToCart && swatch.dataset.imageSrc) addToCart.dataset.productVariantMedia = swatch.dataset.imageSrc;
  }

  animateValue(element) {
    if (this.reduceMotion || !element.animate) return;

    element.getAnimations().forEach((animation) => animation.cancel());
    element.animate(
      [
        { opacity: 0, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 260, easing: 'cubic-bezier(.2, .72, .2, 1)' }
    );
  }

  preloadSwatchImage(swatch) {
    const nextSrc = swatch.dataset.imageSrc;
    const nextSrcset = swatch.dataset.imageSrcset || '';
    if (!nextSrc) return Promise.resolve(false);

    const cacheKey = `${nextSrc}|${nextSrcset}`;
    if (this.imageCache.has(cacheKey)) return this.imageCache.get(cacheKey);

    const panel = swatch.closest('[data-tooto-product-panel]');
    const currentImage = panel?.querySelector('[data-tooto-product-image]');
    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    if (nextSrcset) preloadImage.srcset = nextSrcset;
    if (currentImage?.sizes) preloadImage.sizes = currentImage.sizes;

    const loadPromise = new Promise((resolve) => {
      preloadImage.onload = async () => {
        try {
          await preloadImage.decode();
        } catch (_error) {
          // The load event already confirms the image is usable.
        }
        resolve(true);
      };
      preloadImage.onerror = () => resolve(false);
      preloadImage.src = nextSrc;
    });

    this.imageCache.set(cacheKey, loadPromise);
    return loadPromise;
  }

  async switchImage(image, swatch) {
    const nextSrc = swatch.dataset.imageSrc;
    const nextSrcset = swatch.dataset.imageSrcset || '';
    if (!nextSrc) return;

    const resolvedNextSrc = new URL(nextSrc, document.baseURI).href;
    if (image.src === resolvedNextSrc && image.srcset === nextSrcset) return;

    const media = image.closest('.tooto-featured-products__media');
    const requestId = (this.imageRequestIds.get(image) || 0) + 1;
    this.imageRequestIds.set(image, requestId);
    media?.classList.add('is-loading');
    media?.setAttribute('aria-busy', 'true');

    const isLoaded = await this.preloadSwatchImage(swatch);
    if (this.imageRequestIds.get(image) !== requestId) return;

    if (!isLoaded) {
      media?.classList.remove('is-loading');
      media?.removeAttribute('aria-busy');
      return;
    }

    image.getAnimations().forEach((animation) => animation.cancel());
    image.srcset = nextSrcset;
    image.src = nextSrc;
    if (swatch.dataset.imageAlt) image.alt = swatch.dataset.imageAlt;

    try {
      await image.decode();
    } catch (_error) {
      // Keep the loaded image even when decode() is unavailable or interrupted.
    }

    if (this.imageRequestIds.get(image) !== requestId) return;
    media?.classList.remove('is-loading');
    media?.removeAttribute('aria-busy');

    if (this.reduceMotion || !image.animate) return;

    const animation = image.animate(
      [
        { opacity: .45, transform: 'scale(.99)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 180, easing: 'ease-out' }
    );
    animation.finished.catch(() => {}).finally(() => animation.cancel());
  }
}

if (!customElements.get('tooto-featured-products')) {
  customElements.define('tooto-featured-products', TootoFeaturedProducts);
}
